/**
 * DelegatedAssessmentPage — Generic data gathering form for delegated workflows (DRR-220)
 *
 * Implements the delegation model pattern:
 *   Data Gatherer fills form → Presidio tokenizes → AI generates draft
 *   → Remote Reviewer reviews tokenized draft → Final Approver finalizes
 *
 * Context-driven: Form schema, role labels, and legal basis come from
 * the task's workflow context (constellation delegation_model + bound ruleset).
 *
 * Route: /ferp-core/workflow/delegated-assessment?taskId=...
 */

import { useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Text,
  Stack,
  Alert,
  Button,
  Group,
  Badge,
  LoadingOverlay,
  Stepper,
  Modal,
  List,
  ThemeIcon,
  Skeleton,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCheck,
  IconClipboardCheck,
  IconBrain,
  IconShieldCheck,
  IconFileDescription,
} from '@tabler/icons-react';

import { useTenantId, useAuth } from '@/auth';
import { useResolvedTerminology } from '@/hooks/useResolvedTerminology';
import { UniversalEntityForm } from '@/components/UniversalEntityForm';
import {
  useGetTaskForDetailPageQuery,
  useGenerateIdMutation,
  useCreateFhirStorageResourceMutation,
  IdBrokerEntityType,
} from '@/generated/graphql';

// =============================================================================
// Types
// =============================================================================

interface DelegationContext {
  /** Form schema subgraph (e.g., "fhir", "erp") */
  subgraph: string;
  /** Form schema entity type (e.g., "DataGatheringAssessment") */
  entityType: string;
  /** Role label for data gatherer (e.g., "LPN", "Paralegal", "Analyst") */
  dataGathererLabel: string;
  /** Role label for remote reviewer (e.g., "USRN", "Associate Attorney") */
  remoteReviewerLabel: string;
  /** Role label for final approver (e.g., "DoN", "Partner", "Controller") */
  finalApproverLabel: string;
  /** Legal compliance text (from bound ruleset) */
  complianceText: string;
  /** Statute reference (e.g., "MN 148.171", "SOX 404") */
  statuteRef: string;
}

interface DraftResult {
  draft_id: string;
  subject_id: string;
  assessment_observation_id: string;
  draft_content: Record<string, unknown>;
  generated_at: string;
  model_used: string;
  token_count: number;
  requires_review: boolean;
}

// =============================================================================
// Context Resolution
// =============================================================================

/**
 * Extract delegation context from task metadata.
 * Falls back to generic labels if not specified.
 */
function resolveDelegationContext(
  taskMetadata: Record<string, unknown> | null | undefined,
  taskGroupRef: string | null | undefined
): DelegationContext {
  const meta = taskMetadata || {};
  const delegationModel = (meta.delegation_model || {}) as Record<string, unknown>;

  // Determine subgraph from task group prefix (tg-{subgraph}-...)
  // Default to 'fhir' for healthcare, but could be 'erp', 'legal', etc.
  const subgraphFromTaskGroup = taskGroupRef?.split('-')[1] || '4';
  const subgraphMap: Record<string, string> = {
    '0': 'core',
    '2': 'erp',
    '3': 'legal',
    '4': 'fhir',
    '5': 'construction',
  };

  return {
    subgraph: (delegationModel.subgraph as string) || subgraphMap[subgraphFromTaskGroup] || 'fhir',
    entityType: (delegationModel.entity_type as string) || 'DataGatheringAssessment',
    dataGathererLabel: (delegationModel.data_gatherer_label as string) || 'Data Gatherer',
    remoteReviewerLabel: (delegationModel.remote_reviewer_label as string) || 'Remote Reviewer',
    finalApproverLabel: (delegationModel.final_approver_label as string) || 'Approver',
    complianceText: (delegationModel.compliance_text as string) ||
      'This assessment collects data for professional review. You are gathering observations — professional judgment will be made by the designated reviewer.',
    statuteRef: (delegationModel.statute_ref as string) || '',
  };
}

// =============================================================================
// Agent Service Client
// =============================================================================

const AGENT_SERVICE_URL = import.meta.env.VITE_AGENT_SERVICE_URL || 'http://localhost:8100';

async function generateDraft(
  assessmentObservationId: string,
  subjectId: string,
  authToken: string,
  workflowType: string
): Promise<DraftResult> {
  const response = await fetch(`${AGENT_SERVICE_URL}/agent/delegated-workflow/generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({
      subject_id: subjectId,
      assessment_observation_id: assessmentObservationId,
      workflow_type: workflowType,
    }),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Draft generation failed: ${error}`);
  }

  return response.json();
}

// =============================================================================
// Component
// =============================================================================

export function DelegatedAssessmentPage() {
  const tenantId = useTenantId();
  const { user, accessToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getLabel } = useResolvedTerminology();

  // Get task ID from URL
  const taskId = searchParams.get('taskId') || '';
  const subjectId = searchParams.get('subjectId') || '';
  const subjectName = searchParams.get('subjectName') || getLabel('Patient');

  // Load task to get workflow context (using generated hook)
  const { data: taskData, loading: taskLoading } = useGetTaskForDetailPageQuery({
    variables: { taskId },
    skip: !taskId,
  });

  // Resolve delegation context from task
  const delegationContext = useMemo(() => {
    const task = taskData?.task;
    let parsedMetadata: Record<string, unknown> | null = null;
    if (task?.metadata) {
      try {
        parsedMetadata = typeof task.metadata === 'string'
          ? JSON.parse(task.metadata)
          : task.metadata as Record<string, unknown>;
      } catch {
        parsedMetadata = null;
      }
    }
    return resolveDelegationContext(parsedMetadata, task?.taskGroupRef);
  }, [taskData]);

  // State
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draftResult, setDraftResult] = useState<DraftResult | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // GraphQL mutations (using generated hooks)
  const [createFhirResource] = useCreateFhirStorageResourceMutation();
  const [generateId] = useGenerateIdMutation();

  // Build FHIR Observation from form values
  const buildFhirObservation = useCallback(
    (values: Record<string, unknown>, observationId: string) => {
      return {
        resourceType: 'Observation',
        id: observationId,
        status: 'final',
        category: [
          {
            coding: [
              {
                system: 'http://terminology.hl7.org/CodeSystem/observation-category',
                code: 'survey',
                display: 'Survey',
              },
            ],
            text: `${delegationContext.dataGathererLabel} Assessment`,
          },
        ],
        code: {
          coding: [
            {
              system: 'http://ferp.io/fhir/observation-type',
              code: 'delegated-assessment',
              display: 'Delegated Assessment (DRR-220)',
            },
          ],
          text: `${delegationContext.dataGathererLabel} Assessment`,
        },
        subject: {
          reference: `Patient/${subjectId}`,
        },
        effectiveDateTime: new Date().toISOString().split('T')[0],
        performer: [
          {
            reference: `Practitioner/${user?.id || 'unknown'}`,
            display: user?.displayName || delegationContext.dataGathererLabel,
          },
        ],
        extension: [
          {
            url: 'http://ferp.io/fhir/StructureDefinition/assessment-full-data',
            valueString: JSON.stringify(values),
          },
          {
            url: 'http://ferp.io/fhir/StructureDefinition/task-ref',
            valueString: taskId,
          },
        ],
      };
    },
    [subjectId, user, delegationContext, taskId]
  );

  // Handle form submission
  const handleSubmit = useCallback(
    async (values: Record<string, unknown>) => {
      setSubmitting(true);
      setError(null);

      try {
        // Generate observation ID (using 'correlation' for cross-resource FHIR correlation)
        const idResult = await generateId({
          variables: {
            input: { entityType: IdBrokerEntityType.correlation, count: 1 },
          },
        });
        const observationId = idResult.data?.generateId?.ids?.[0];
        if (!observationId) {
          throw new Error('Failed to generate observation ID');
        }

        // Build and save FHIR Observation
        const fhirObservation = buildFhirObservation(values, observationId);

        // Extract tenant number from tenantId
        const tenantNum = parseInt(tenantId?.split('_')[0] || '17', 10);

        await createFhirResource({
          variables: {
            input: {
              resourceType: 'Observation',
              resourceId: observationId,
              fhirJson: JSON.stringify(fhirObservation),
              tenantNum,
              createdBy: user?.id || 'unknown',
            },
          },
        });

        setStep(1); // Move to generation step

        // Auto-trigger draft generation
        setGenerating(true);
        try {
          const draft = await generateDraft(
            observationId,
            subjectId,
            accessToken || '',
            delegationContext.entityType
          );
          setDraftResult(draft);
          setStep(2);
          setShowSuccessModal(true);
        } catch (genError) {
          console.error('Draft generation failed:', genError);
          setError(
            `Assessment saved, but draft generation failed: ${genError instanceof Error ? genError.message : 'Unknown error'}. You can retry from the review queue.`
          );
        } finally {
          setGenerating(false);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save assessment');
      } finally {
        setSubmitting(false);
      }
    },
    [buildFhirObservation, createFhirResource, generateId, subjectId, accessToken, tenantId, user, delegationContext]
  );

  // No task selected
  if (!taskId) {
    return (
      <Container size="md" py="xl">
        <Alert color="yellow" title="No Task Selected" icon={<IconFileDescription size={16} />}>
          <Text mb="md">Please access this page from your task list.</Text>
          <Button variant="light" onClick={() => navigate('/ferp-core/tasks')}>
            View Tasks
          </Button>
        </Alert>
      </Container>
    );
  }

  // Loading task context
  if (taskLoading) {
    return (
      <Container size="lg" py="xl">
        <Stack gap="lg">
          <Skeleton height={40} />
          <Skeleton height={200} />
          <Skeleton height={400} />
        </Stack>
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <div>
            <Group gap="sm" mb={4}>
              <IconClipboardCheck size={28} />
              <Title order={2}>{taskData?.task?.name || 'Assessment'}</Title>
            </Group>
            <Text c="dimmed" size="sm">
              Data gathering for {subjectName}
            </Text>
          </div>
          <Badge color="blue" variant="light" leftSection={<IconShieldCheck size={12} />}>
            DRR-220 Role-Isolated
          </Badge>
        </Group>

        {/* Progress Stepper */}
        <Paper p="md" withBorder>
          <Stepper active={step} size="sm">
            <Stepper.Step
              icon={<IconClipboardCheck size={18} />}
              label="Assessment"
              description="Complete form"
            />
            <Stepper.Step
              icon={<IconBrain size={18} />}
              label="AI Generation"
              description="Generate draft"
              loading={generating}
            />
            <Stepper.Step
              icon={<IconCheck size={18} />}
              label="Complete"
              description="Sent for review"
            />
          </Stepper>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert
            color="red"
            title="Error"
            icon={<IconAlertTriangle size={16} />}
            withCloseButton
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Form (Step 0) */}
        {step === 0 && (
          <Paper p="lg" withBorder pos="relative">
            <LoadingOverlay visible={submitting} />

            {/* Delegation compliance notice */}
            <Alert color="blue" mb="lg" icon={<IconShieldCheck size={16} />}>
              {delegationContext.statuteRef && (
                <Text size="sm" fw={500}>
                  {delegationContext.statuteRef} Delegation Compliance
                </Text>
              )}
              <Text size="sm">{delegationContext.complianceText}</Text>
            </Alert>

            <UniversalEntityForm
              subgraph={delegationContext.subgraph}
              entityType={delegationContext.entityType}
              onSubmit={handleSubmit}
              onCancel={() => navigate(-1)}
              initialValues={{
                assessment_date: new Date().toISOString().split('T')[0],
              }}
              title={`${delegationContext.dataGathererLabel} Assessment`}
            />
          </Paper>
        )}

        {/* Generation Progress (Step 1) */}
        {step === 1 && generating && (
          <Paper p="xl" withBorder>
            <Stack align="center" gap="lg">
              <IconBrain size={48} color="var(--mantine-color-blue-6)" />
              <Title order={3}>Generating Draft</Title>
              <Text c="dimmed" ta="center">
                AI is analyzing the assessment data and generating a draft. This typically takes
                15-30 seconds.
              </Text>
              <LoadingOverlay visible={true} loaderProps={{ size: 'lg' }} />
            </Stack>
          </Paper>
        )}

        {/* Success Modal */}
        <Modal
          opened={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          title={
            <Group gap="sm">
              <ThemeIcon color="green" size="lg" radius="xl">
                <IconCheck size={20} />
              </ThemeIcon>
              <Text fw={600}>Assessment Complete</Text>
            </Group>
          }
          centered
          size="lg"
        >
          <Stack gap="md">
            <Text>
              Your assessment has been saved and a draft has been generated for{' '}
              {delegationContext.remoteReviewerLabel} review.
            </Text>

            {draftResult && (
              <Alert color="blue" variant="light">
                <Text size="sm" fw={500} mb={4}>
                  Draft Summary
                </Text>
                <List size="sm" spacing={4}>
                  <List.Item>Draft ID: {draftResult.draft_id}</List.Item>
                  <List.Item>Generated: {draftResult.generated_at}</List.Item>
                  <List.Item>
                    Status: {draftResult.requires_review ? 'Pending Review' : 'Auto-approved'}
                  </List.Item>
                </List>
              </Alert>
            )}

            <Alert color="yellow" variant="light" icon={<IconShieldCheck size={16} />}>
              <Text size="sm">
                <strong>Next Steps:</strong> The draft will be reviewed by the{' '}
                {delegationContext.remoteReviewerLabel} who will make professional adjustments. The{' '}
                {delegationContext.finalApproverLabel} will then finalize with the subject.
              </Text>
            </Alert>

            <Group justify="flex-end" mt="md">
              <Button variant="light" onClick={() => navigate('/ferp-core/tasks')}>
                View My Tasks
              </Button>
              <Button onClick={() => navigate(-1)}>Done</Button>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </Container>
  );
}

export default DelegatedAssessmentPage;
